import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isEngineRoute } from "@/i18n/languages";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isNative } from "@/lib/appConfig";
import storage from "@/lib/storage";

const STORAGE_KEY = "qc_cookie_consent_v1";

export const getCookieConsent = () => {
    try {
        const v = storage.getItem(STORAGE_KEY);
        return v ? JSON.parse(v) : null;
    } catch {
        return null;
    }
};

export const setCookieConsent = (consent) => {
    const payload = { ...consent, decided_at: new Date().toISOString() };
    storage.setItem(STORAGE_KEY, JSON.stringify(payload));
    try { window.dispatchEvent(new CustomEvent("qc_cookie_consent_change", { detail: payload })); } catch (_e) {}
    return payload;
};

// La version inglesa solo sale en /en/*. La pagina legal de cookies solo existe en espanol y el texto lo dice.
const COPY = {
    es: {
        label: "Aviso de cookies",
        title: "Usamos cookies",
        intro: "Usamos cookies estrictamente necesarias para que la plataforma funcione (sesión, preferencias). Otras cookies de analítica o marketing son opcionales y solo se activan si las aceptas.",
        more: "Más info",
        necessary: "Necesarias",
        necessaryHelp: "Sesión y preferencias. Siempre activas.",
        analytics: "Analítica",
        analyticsHelp: "Analítica web con Google Analytics 4: métricas de uso con identificadores seudónimos.",
        marketing: "Marketing",
        marketingHelp: "Píxel de Meta: mide la eficacia de nuestros anuncios en Facebook e Instagram.",
        acceptAll: "Aceptar todas",
        rejectAll: "Rechazar opcionales",
        configure: "Configurar",
        save: "Guardar preferencias",
    },
    en: {
        label: "Cookie notice",
        title: "We use cookies",
        intro: "We use strictly necessary cookies so the platform works (session, preferences). Other analytics or marketing cookies are optional and are only switched on if you accept them.",
        more: "More information (the cookie policy is available in Spanish only)",
        necessary: "Necessary",
        necessaryHelp: "Session and preferences. Always on.",
        analytics: "Analytics",
        analyticsHelp: "Web analytics with Google Analytics 4: usage metrics with pseudonymous identifiers.",
        marketing: "Marketing",
        marketingHelp: "Meta pixel: measures how well our ads work on Facebook and Instagram.",
        acceptAll: "Accept all",
        rejectAll: "Reject optional",
        configure: "Configure",
        save: "Save preferences",
    },
};

// En el Engine manda el idioma activo. Su acceso no lo cambia (useEngineAuthText): ahi solo lo dice el enlace.
function bannerLang(pathname, search, language) {
    if (pathname === "/en" || pathname.startsWith("/en/") || language === "en") return "en";
    const params = new URLSearchParams(search);
    return isEngineRoute(params.get("from")) && params.get("lang") === "en" ? "en" : "es";
}

export default function CookieBanner() {
    const { pathname, search } = useLocation();
    const { i18n } = useTranslation();
    const c = COPY[bannerLang(pathname, search, i18n.language)];
    const [open, setOpen] = useState(false);
    const [showPrefs, setShowPrefs] = useState(false);
    const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

    useEffect(() => {
        if (isNative()) return; // cookie consent is a web concept; native uses store privacy labels
        const existing = getCookieConsent();
        if (!existing) setOpen(true);
    }, []);

    const acceptAll = () => {
        setCookieConsent({ necessary: true, analytics: true, marketing: true });
        setOpen(false);
    };
    const rejectAll = () => {
        setCookieConsent({ necessary: true, analytics: false, marketing: false });
        setOpen(false);
    };
    const savePrefs = () => {
        setCookieConsent(prefs);
        setOpen(false);
    };

    if (!open) return null;
    return (
        <div
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[100] bg-bone border border-soft_border rounded-2xl shadow-hover p-5"
            role="dialog"
            aria-live="polite"
            aria-label={c.label}
            data-testid="cookie-banner"
        >
            <div className="flex items-start gap-3">
                <Cookie className="w-5 h-5 text-petrol mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="flex-1">
                    <p className="font-display text-lg text-graphite leading-tight">{c.title}</p>
                    <p className="text-xs text-soft_muted mt-2 leading-relaxed">
                        {c.intro}{" "}
                        <Link to="/legal/cookies" className="text-petrol underline">{c.more}</Link>
                    </p>

                    {showPrefs && (
                        <div className="mt-4 space-y-2 bg-stone border border-soft_border rounded-lg p-3 text-xs" data-testid="cookie-prefs">
                            <PrefRow label={c.necessary} value={true} disabled help={c.necessaryHelp} />
                            <PrefRow label={c.analytics} value={prefs.analytics} onChange={(v) => setPrefs({ ...prefs, analytics: v })} help={c.analyticsHelp} testId="pref-analytics" />
                            <PrefRow label={c.marketing} value={prefs.marketing} onChange={(v) => setPrefs({ ...prefs, marketing: v })} help={c.marketingHelp} testId="pref-marketing" />
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2 mt-4">
                        <Button size="sm" onClick={acceptAll} className="bg-petrol hover:bg-petrol-hover text-bone flex-1 sm:flex-initial" data-testid="cookie-accept-all">{c.acceptAll}</Button>
                        <Button size="sm" variant="outline" onClick={rejectAll} className="border-soft_border flex-1 sm:flex-initial" data-testid="cookie-reject-all">{c.rejectAll}</Button>
                        {!showPrefs ? (
                            <button onClick={() => setShowPrefs(true)} className="text-xs text-soft_muted hover:text-graphite px-2" data-testid="cookie-preferences">{c.configure}</button>
                        ) : (
                            <Button size="sm" variant="outline" onClick={savePrefs} className="border-petrol/40 text-petrol" data-testid="cookie-save-prefs">{c.save}</Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function PrefRow({ label, value, onChange, disabled, help, testId }) {
    return (
        <label className="flex items-center justify-between gap-2 cursor-pointer">
            <div className="min-w-0">
                <p className="text-sm text-graphite">{label}</p>
                <p className="text-[10px] text-soft_muted">{help}</p>
            </div>
            <input
                type="checkbox"
                checked={!!value}
                disabled={disabled}
                onChange={(e) => onChange?.(e.target.checked)}
                className="accent-petrol w-4 h-4"
                data-testid={testId}
            />
        </label>
    );
}
